import {
  BaseDecisionPriority,
  DecisionContext,
  DecisionResult,
  MoveInfo,
} from "..\/..\/interfaces\/DecisionPriority";
import {
  cloneFullGameState,
  getPieceFromPosition,
  validateMeadowEffects,
} from "..\/..\/..\/utils\/MCTSUtils";
import { MCTSMoveGenerationService } from "..\/..\/..\/utils\/MCTSMoveGenerationService";
import { MoveType } from "..\/..\/..\/..\/types";

/**
 * Priority 13: Hang-Stay Defense
 *
 * DEFENSIVE CONCEPT: When we're materially down and our pieces are hanging (under enemy attack),
 * move them to safety if it's not a beneficial trade.
 *
 * Logic:
 * 1. Check if we're materially down
 * 2. Find our pieces that are under enemy attack (hanging)
 * 3. If the trade isn't beneficial, defensively move the piece or defend it
 * 4. Comes after Free Capture so we still take free material first
 */
export class HangStayPriority extends BaseDecisionPriority {
  readonly priority = 18;
  readonly name = "Hang-Stay Defense";
  readonly description =
    "Defensively move hanging pieces when materially down and trade isn't beneficial";

  private analysisLogs: string[] = [];

  isRelevant(context: DecisionContext): boolean {
    return true; // Always check if we have hanging pieces to protect
  }

  analyze(context: DecisionContext): DecisionResult {
    this.analysisLogs = [];
    this.addLog(
      "🛡️ HANG-STAY DEFENSE: Starting analysis of our hanging pieces"
    );

    const gameState = context.gameState;

    // First, check if we're materially down
    const materialStatus = this.getMaterialStatus(gameState);
    this.addLog(
      `📊 Material status: Us ${materialStatus.ourMaterial} vs Enemy ${
        materialStatus.enemyMaterial
      } (${materialStatus.difference >= 0 ? "+" : ""}${
        materialStatus.difference
      })`
    );

    if (materialStatus.difference >= 0) {
      this.addLog(
        "✅ We're not materially down - no defensive hanging moves needed"
      );
      return this.createNoActionResult(
        "Not materially down - no hanging defense required"
      );
    }

    this.addLog(
      `🚨 We're materially DOWN by ${Math.abs(
        materialStatus.difference
      )} - checking for hanging pieces to protect`
    );

    // Find our pieces that are hanging (under enemy attack)
    const ourHangingPieces = this.findOurHangingPieces(gameState);

    if (ourHangingPieces.length === 0) {
      this.addLog("✅ No hanging pieces found - no defensive moves needed");
      return this.createNoActionResult("No hanging pieces to protect");
    }

    this.addLog(
      `🔍 Found ${ourHangingPieces.length} hanging pieces to potentially protect:`
    );
    for (const hanging of ourHangingPieces) {
      this.addLog(
        `   📍 ${hanging.piece} at [${hanging.position.row},${
          hanging.position.col
        }] - threatened by ${hanging.threatenedBy.join(", ")}`
      );
    }

    // Find defensive moves for hanging pieces
    const defensiveMoves = this.findDefensiveMoves(
      context.allPossibleMoves,
      gameState,
      ourHangingPieces
    );

    if (defensiveMoves.length === 0) {
      this.addLog("❌ No defensive moves available for hanging pieces");
      return this.createNoActionResult("No defensive moves possible");
    }

    // Select the best defensive move (prioritize most valuable pieces)
    const bestDefensiveMove = this.selectBestDefensiveMove(
      defensiveMoves,
      gameState
    );

    this.addLog(
      `✅ SELECTING DEFENSIVE MOVE: [${bestDefensiveMove.fromPosition.row},${bestDefensiveMove.fromPosition.col}] -> [${bestDefensiveMove.move.position.row},${bestDefensiveMove.move.position.col}]`
    );

    return this.createEarlyExitResult(
      bestDefensiveMove,
      "Defensive move to protect hanging piece",
      {
        logs: this.analysisLogs,
        hangingPieces: ourHangingPieces.length,
        defensiveMoves: defensiveMoves.length,
        materialDeficit: Math.abs(materialStatus.difference),
      }
    );
  }

  private addLog(message: string): void {
    this.analysisLogs.push(message);
  }

  /**
   * Calculate our material status vs enemy
   */
  private getMaterialStatus(gameState: any): {
    ourMaterial: number;
    enemyMaterial: number;
    difference: number;
  } {
    let ourMaterial = 0;
    let enemyMaterial = 0;
    const isOurTurn = gameState.isLightTurn;

    for (let row = 0; row < gameState.board.length; row++) {
      for (let col = 0; col < gameState.board[row].length; col++) {
        const square = gameState.board[row][col];
        if (square.Pieces && square.Pieces.length > 0) {
          for (const piece of square.Pieces) {
            const pieceValue = this.getPieceValue(piece.type);
            if (piece.isLight === isOurTurn) {
              ourMaterial += pieceValue;
            } else {
              enemyMaterial += pieceValue;
            }
          }
        }
      }
    }

    return {
      ourMaterial,
      enemyMaterial,
      difference: ourMaterial - enemyMaterial,
    };
  }

  /**
   * Get piece value for material calculation (all pieces equal per user's request)
   */
  private getPieceValue(pieceType: string): number {
    return 1; // All pieces equal value as requested
  }

  /**
   * Find our pieces that are currently under enemy attack (hanging)
   */
  private findOurHangingPieces(gameState: any): Array<{
    position: { row: number; col: number };
    piece: string;
    threatenedBy: string[];
  }> {
    const hangingPieces: Array<{
      position: { row: number; col: number };
      piece: string;
      threatenedBy: string[];
    }> = [];

    // Get enemy moves to see what they can attack
    const testGameState = cloneFullGameState(gameState);
    testGameState.isLightTurn = !testGameState.isLightTurn; // Switch to enemy turn

    const moveService = MCTSMoveGenerationService.getInstance();
    const enemyMoves = moveService.getAllMoves(testGameState);

    // Check each of our pieces to see if it's under attack
    for (let row = 0; row < gameState.board.length; row++) {
      for (let col = 0; col < gameState.board[row].length; col++) {
        const square = gameState.board[row][col];
        if (square.Pieces && square.Pieces.length > 0) {
          for (const piece of square.Pieces) {
            // Check if this is our piece
            if (piece.isLight !== gameState.isLightTurn) {
              continue; // This is enemy piece, not ours
            }

            // Check if any enemy moves can capture this piece
            const threateningMoves: string[] = [];
            for (const enemyMove of enemyMoves) {
              if (
                enemyMove.move.position.row === row &&
                enemyMove.move.position.col === col &&
                enemyMove.move.moveType === MoveType.capture
              ) {
                const enemyPiece = getPieceFromPosition(
                  testGameState,
                  enemyMove.fromPosition
                );
                if (enemyPiece) {
                  threateningMoves.push(
                    `${enemyPiece.type} at [${enemyMove.fromPosition.row},${enemyMove.fromPosition.col}]`
                  );
                }
              }
            }

            if (threateningMoves.length > 0) {
              hangingPieces.push({
                position: { row, col },
                piece: `${piece.type}${piece.isKing ? " (King)" : ""}`,
                threatenedBy: threateningMoves,
              });
            }
          }
        }
      }
    }

    return hangingPieces;
  }

  /**
   * Find moves that defensively protect our hanging pieces
   */
  private findDefensiveMoves(
    allMoves: MoveInfo[],
    gameState: any,
    hangingPieces: Array<{
      position: { row: number; col: number };
      piece: string;
      threatenedBy: string[];
    }>
  ): MoveInfo[] {
    const defensiveMoves: MoveInfo[] = [];

    for (const moveInfo of allMoves) {
      const isDefensive = this.isMoveDefensive(
        moveInfo,
        gameState,
        hangingPieces
      );

      if (isDefensive) {
        this.addLog(
          `🛡️ DEFENSIVE MOVE: [${moveInfo.fromPosition.row},${moveInfo.fromPosition.col}] -> [${moveInfo.move.position.row},${moveInfo.move.position.col}] protects hanging piece`
        );
        defensiveMoves.push(moveInfo);
      }
    }

    return defensiveMoves;
  }

  /**
   * Check if a move is defensive (moves a hanging piece to safety or defends it)
   */
  private isMoveDefensive(
    moveInfo: MoveInfo,
    gameState: any,
    hangingPieces: Array<{
      position: { row: number; col: number };
      piece: string;
      threatenedBy: string[];
    }>
  ): boolean {
    // Check if this move involves moving one of our hanging pieces
    for (const hangingPiece of hangingPieces) {
      if (
        moveInfo.fromPosition.row === hangingPiece.position.row &&
        moveInfo.fromPosition.col === hangingPiece.position.col
      ) {
        // This move moves a hanging piece - check if it goes to a safer position
        const wouldBeSaferAfterMove = this.wouldPieceBeScaferAfterMove(
          moveInfo,
          gameState
        );
        if (wouldBeSaferAfterMove) {
          return true;
        }
      }
    }

    // Check if this move defends a hanging piece (blocks enemy attack)
    const defenderPiece = getPieceFromPosition(
      gameState,
      moveInfo.fromPosition
    );
    if (
      defenderPiece &&
      this.moveDefendsHangingPiece(moveInfo, gameState, hangingPieces)
    ) {
      return true;
    }

    return false;
  }

  /**
   * Check if a piece would be safer after moving to the target position
   */
  private wouldPieceBeScaferAfterMove(
    moveInfo: MoveInfo,
    gameState: any
  ): boolean {
    try {
      // Simulate the move
      const testGameState = cloneFullGameState(gameState);
      const ourPiece = getPieceFromPosition(
        testGameState,
        moveInfo.fromPosition
      );

      if (!ourPiece) return false;

      // Apply move manually (avoid broken applyMoveWithSnakeRespawn)
      const fromSquare =
        testGameState.board[moveInfo.fromPosition.row][
          moveInfo.fromPosition.col
        ];
      const toSquare =
        testGameState.board[moveInfo.move.position.row][
          moveInfo.move.position.col
        ];

      // Handle captures if any
      if (moveInfo.move.toPiece) {
        toSquare.Pieces = toSquare.Pieces.filter(
          (p) => p.id !== moveInfo.move.toPiece!.id
        );
      }

      fromSquare.Pieces = fromSquare.Pieces.filter((p) => p.id !== ourPiece.id);
      toSquare.Pieces.push(ourPiece);

      // Switch to enemy turn and check if they can still attack our piece
      testGameState.isLightTurn = !testGameState.isLightTurn;

      const moveService = MCTSMoveGenerationService.getInstance();
      const enemyMoves = moveService.getAllMoves(testGameState);

      const newPosition = moveInfo.move.position;

      // Check if enemy can still capture our piece at the new position
      for (const enemyMove of enemyMoves) {
        if (
          enemyMove.move.position.row === newPosition.row &&
          enemyMove.move.position.col === newPosition.col &&
          enemyMove.move.moveType === MoveType.capture
        ) {
          return false; // Still under attack at new position
        }
      }

      return true; // Safer at new position
    } catch (error) {
      this.addLog(`❌ Error checking move safety: ${error}`);
      return false;
    }
  }

  /**
   * Check if a move defends a hanging piece (e.g., by blocking or defending)
   */
  private moveDefendsHangingPiece(
    moveInfo: MoveInfo,
    gameState: any,
    hangingPieces: Array<{
      position: { row: number; col: number };
      piece: string;
      threatenedBy: string[];
    }>
  ): boolean {
    // For now, focus on moving hanging pieces to safety
    // Defending by interposition/blocking could be added later
    return false;
  }

  /**
   * Select the best defensive move (prioritize protecting more valuable pieces)
   */
  private selectBestDefensiveMove(
    defensiveMoves: MoveInfo[],
    gameState: any
  ): MoveInfo {
    // For now, just return the first defensive move
    // Could be enhanced to prioritize by piece value, king safety, etc.
    return defensiveMoves[0];
  }
}

